/* eslint-disable max-lines */
import * as React from "react";
import { connect } from "react-redux";
import { values } from "lodash";

import { OldProgressBarQuestionnaireSectionUnconnected } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireSection";
import { getQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { mapDispatchToQuestionnaireSectionProps, makeMapStateToQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/mappers";
import { CustomQuestionsQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/QuestionnaireSectionProps";
import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { YesNoWithFollowUpQuestion } from "@teamprevent/common/questionnaires/components/questions/YesNoWithFollowUpQuestion";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { checkExistenceOfQuestionWithId } from "@teamprevent/common/questionnaires/checkExistenceOfQuestionWithId";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { ImageAnnotatorQuestion } from "@teamprevent/common/questionnaires/components/questions/imageAnnotatorQuestion/ImageAnnotatorQuestion";
import { MultiCheckboxQuestion, MultiCheckbox } from "@teamprevent/common/questionnaires/components/questions/MultiCheckboxQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import {
    HAVS_TIERS,
    HAVS_TIER_OPTIONS,
    HAVS_TIER_ONE_OUTCOME_OPTIONS,
    HAVS_TIER_TWO_OUTCOME_OPTIONS,
    RIGHT_LEFT_HAND_OPTIONS,
    RIGHT_LEFT_OPTIONS
} from "@teamprevent/common/healthSurveillance/models";

import { HealthSurveillanceHandsImageUrl } from "../models";
import * as questionIds from "../consultationQuestions/questionIds";
import { sections } from "../consultation";

// eslint-disable-next-line max-len
const HealthSurveillanceConsultationHavsSectionUnconnected: React.FunctionComponent<CustomQuestionsQuestionnaireSectionProps> = (props: CustomQuestionsQuestionnaireSectionProps) => {
    const questions = values(props.questions || {});
    const assessmentRequiredAnswer = "Issue Identified - HAVS Assessment Required";
    const assessmentNotRequiredAnswer = "No Issue Identified - HAVS Assessment Not Required";
    const isOldVersion = (props.readonly && checkExistenceOfQuestionWithId(questions, questionIds.HavsAssessmentRequired));
    return (
        <OldProgressBarQuestionnaireSectionUnconnected
            {...props}
            {...sections.Havs}
        >
            {isOldVersion &&
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HavsAssessmentRequired)}
                    options={[assessmentNotRequiredAnswer, assessmentRequiredAnswer].map(a => ({ text: a, value: a }))}
                    mandatory
                />
            }
            {!isOldVersion &&
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HavsTier)}
                    options={HAVS_TIER_OPTIONS}
                    mandatory
                />
            }
            {((isOldVersion &&
                getAnswerToQuestionWithId(questions, questionIds.HavsAssessmentRequired) === assessmentRequiredAnswer) ||
                !isOldVersion) &&
                <>
                    {(getAnswerToQuestionWithId(questions, questionIds.HavsTier) === HAVS_TIERS.TierOne ||
                        getAnswerToQuestionWithId(questions, questionIds.HavsTier) === HAVS_TIERS.TierTwo) &&
                        <>
                            <TextQuestion
                                readonly={props.readonly}
                                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ClinicalAssessmentOfHavsSignsAndSymptomReview)}
                                multiline
                            />
                            {getAnswerToQuestionWithId(questions, questionIds.HavsTier) === HAVS_TIERS.TierOne &&
                                <DropdownQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.TierOneOutcome)}
                                    options={HAVS_TIER_ONE_OUTCOME_OPTIONS}
                                />
                            }
                            {getAnswerToQuestionWithId(questions, questionIds.HavsTier) === HAVS_TIERS.TierTwo &&
                                <DropdownQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.TierTwoOutcome)}
                                    options={HAVS_TIER_TWO_OUTCOME_OPTIONS}
                                />
                            }
                            <TextQuestion
                                readonly={props.readonly}
                                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AdditionalAdviceAndRecallPlan)}
                                multiline
                            />
                        </>
                    }
                    {(isOldVersion ||
                        getAnswerToQuestionWithId(questions, questionIds.HavsTier) === HAVS_TIERS.TierThree ||
                        getAnswerToQuestionWithId(questions, questionIds.HavsTier) === HAVS_TIERS.TierFour) &&
                        <>
                            <MultiButtonQuestion
                                readonly={props.readonly}
                                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.EverSufferedBlanching)}
                                mandatory={isOldVersion}
                            />
                            {getAnswerToQuestionWithId(questions, questionIds.EverSufferedBlanching) === MultiButtonQuestion.YesOption &&
                                <>
                                    <p>If yes (and still occurring in the last 2 years) does it occur:</p>
                                    <MultiButtonQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BlanchingInResponseToColdDampOrWet)}
                                        mandatory={isOldVersion}
                                    />
                                    <MultiButtonQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BlanchingWhileWorking)}
                                        mandatory={isOldVersion}
                                    />
                                    <MultiButtonQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BlanchingOtherTimes)}
                                        mandatory={isOldVersion}
                                    />
                                    {getAnswerToQuestionWithId(questions, questionIds.BlanchingOtherTimes) === MultiButtonQuestion.YesOption &&
                                        <TextQuestion
                                            readonly={props.readonly}
                                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BlanchingOtherTimesExamples)}
                                            mandatory={isOldVersion}
                                        />
                                    }
                                    <TextQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BlanchingFirstNoticed)}
                                        mandatory={isOldVersion}
                                    />
                                    <MultiCheckboxQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BlanchingHowOften)}
                                        mandatory={isOldVersion}
                                    >
                                        <MultiCheckbox value="Several times a year" label="Several times a year" />
                                        <MultiCheckbox value="Several times a month" label="Several times a month" />
                                        <MultiCheckbox value="Several times a day" label="Several times a day" />
                                        <MultiCheckbox value="Every day" label="Every day" />
                                        <MultiCheckbox value="Winter only" label="Winter only" />
                                        <MultiCheckbox value="Winter and summer" label="Winter and summer" />
                                    </MultiCheckboxQuestion>
                                    <TextQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BlanchingMostCommonCircumstances)}
                                        mandatory={isOldVersion}
                                    />
                                    <MultiButtonQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ExperienceWhitenessInFeetOrOtherPeriphery)}
                                        mandatory={isOldVersion}
                                    />
                                    {
                                        // eslint-disable-next-line max-len
                                        getAnswerToQuestionWithId(questions, questionIds.ExperienceWhitenessInFeetOrOtherPeriphery) === MultiButtonQuestion.YesOption &&
                                        <TextQuestion
                                            readonly={props.readonly}
                                            questionConfig={
                                                getQuestionConfig(props.questionConfigs, questionIds.ExperienceWhitenessInFeetOrOtherPeripheryWhere)
                                            }
                                            mandatory={isOldVersion}
                                        />
                                    }
                                    <ImageAnnotatorQuestion
                                        imageHref={HealthSurveillanceHandsImageUrl}
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.WhichFingersAffectedByBlanching)}
                                    />
                                    {isOldVersion &&
                                        <>
                                            <CheckboxQuestion
                                                readonly={props.readonly}
                                                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BlanchingWitnessed)}
                                                mandatory
                                                value="Yes"
                                            />
                                            <CheckboxQuestion
                                                readonly={props.readonly}
                                                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BlanchingNotWitnessed)}
                                                mandatory
                                                value="Yes"
                                            />
                                        </>
                                    }
                                    {!isOldVersion &&
                                        <MultiButtonQuestion
                                            readonly={props.readonly}
                                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BlanchingWitnessedByPersonCompletingScreening)}
                                        />
                                    }
                                </>
                            }
                            <MultiButtonQuestion
                                readonly={props.readonly}
                                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Tingling)}
                                mandatory={isOldVersion}
                            />
                            {getAnswerToQuestionWithId(questions, questionIds.Tingling) === MultiButtonQuestion.YesOption &&
                                <>
                                    <MultiButtonQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ExperienceTinglingOfFingers)}
                                        mandatory={isOldVersion}
                                    />
                                    <MultiButtonQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.TinglingResponseToCold)}
                                        mandatory={isOldVersion}
                                    />
                                    <MultiButtonQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.TinglingWithBlanching)}
                                        mandatory={isOldVersion}
                                    />
                                    <MultiButtonQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.TinglingWhileWorking)}
                                        mandatory={isOldVersion}
                                    />
                                    {YesNoWithFollowUpQuestion({
                                        questionConfigs: [
                                            getQuestionConfig(props.questionConfigs, questionIds.TinglingAtOtherTimes),
                                            getQuestionConfig(props.questionConfigs, questionIds.TinglingAtOtherTimesExamples)
                                        ],
                                        readonly: props.readonly,
                                        questions: props.questions
                                    })}
                                    <TextQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.TinglingWhenFirstNoticed)}
                                        mandatory={isOldVersion}
                                    />
                                    {isOldVersion &&
                                        <ImageAnnotatorQuestion
                                            imageHref={HealthSurveillanceHandsImageUrl}
                                            readonly={props.readonly}
                                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.TinglingWhichFingersAffected)}
                                        />
                                    }
                                    {!isOldVersion &&
                                        <ImageAnnotatorQuestion
                                            imageHref={HealthSurveillanceHandsImageUrl}
                                            readonly={props.readonly}
                                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.TinglingWhichFingersAffectedUpdated)}
                                        />
                                    }
                                </>
                            }
                            <MultiButtonQuestion
                                readonly={props.readonly}
                                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Numbness)}
                                mandatory={isOldVersion}
                            />
                            {getAnswerToQuestionWithId(questions, questionIds.Numbness) === MultiButtonQuestion.YesOption &&
                                <>
                                    <MultiButtonQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DoFingersGoNumb)}
                                        mandatory={isOldVersion}
                                    />
                                    <MultiButtonQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.NumbnessResponseToCold)}
                                        mandatory={isOldVersion}
                                    />
                                    <MultiButtonQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.NumbnessWithBlanching)}
                                        mandatory={isOldVersion}
                                    />
                                    <MultiButtonQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.NumbnessWhileWorking)}
                                        mandatory={isOldVersion}
                                    />
                                    {YesNoWithFollowUpQuestion({
                                        questionConfigs: [
                                            getQuestionConfig(props.questionConfigs, questionIds.NumbnessOtherTimes),
                                            getQuestionConfig(props.questionConfigs, questionIds.NumbnessOtherTimesExamples)
                                        ],
                                        readonly: props.readonly,
                                        questions: props.questions
                                    })}
                                    <TextQuestion
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.NumbnessFirstNotice)}
                                        mandatory={isOldVersion}
                                    />
                                    <ImageAnnotatorQuestion
                                        imageHref={HealthSurveillanceHandsImageUrl}
                                        readonly={props.readonly}
                                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.NumbnessWhichFingers)}
                                    />
                                </>
                            }
                            {YesNoWithFollowUpQuestion({
                                questionConfigs: [
                                    getQuestionConfig(props.questionConfigs, questionIds.NumbnessDifficultyHandlingSmallObjects),
                                    getQuestionConfig(props.questionConfigs, questionIds.NumbnessDifficultyHandlingSmallObjectsWhen)
                                ],
                                readonly: props.readonly,
                                questions: props.questions
                            })}
                            {YesNoWithFollowUpQuestion({
                                questionConfigs: [
                                    getQuestionConfig(props.questionConfigs, questionIds.AnyBlanchingTinglingNumbnessAffectWorkOrLeisure),
                                    getQuestionConfig(props.questionConfigs, questionIds.AnyBlanchingTinglingNumbnessAffectWorkOrLeisureFurtherDetails)
                                ],
                                readonly: props.readonly,
                                questions: props.questions
                            })}
                            {YesNoWithFollowUpQuestion({
                                questionConfigs: [
                                    getQuestionConfig(props.questionConfigs, questionIds.ProblemsWithMusclesOrJoints),
                                    getQuestionConfig(props.questionConfigs, questionIds.ProblemsWithMusclesOrJointsFurtherDetails)
                                ],
                                readonly: props.readonly,
                                questions: props.questions
                            })}
                            {getAnswerToQuestionWithId(questions, questionIds.ProblemsWithMusclesOrJoints) === MultiButtonQuestion.YesOption &&
                            <>
                                {YesNoWithFollowUpQuestion({
                                    questionConfigs: [
                                        getQuestionConfig(props.questionConfigs, questionIds.Pain),
                                        getQuestionConfig(props.questionConfigs, questionIds.PainFurtherDetails)
                                    ],
                                    readonly: props.readonly,
                                    questions: props.questions
                                })}
                                {YesNoWithFollowUpQuestion({
                                    questionConfigs: [
                                        getQuestionConfig(props.questionConfigs, questionIds.Stiffness),
                                        getQuestionConfig(props.questionConfigs, questionIds.StiffnessFurtherDetails)
                                    ],
                                    readonly: props.readonly,
                                    questions: props.questions
                                })}
                                {YesNoWithFollowUpQuestion({
                                    questionConfigs: [
                                        getQuestionConfig(props.questionConfigs, questionIds.Swelling),
                                        getQuestionConfig(props.questionConfigs, questionIds.SwellingFurtherDetails)
                                    ],
                                    readonly: props.readonly,
                                    questions: props.questions
                                })}
                                {YesNoWithFollowUpQuestion({
                                    questionConfigs: [
                                        getQuestionConfig(props.questionConfigs, questionIds.Weakness),
                                        getQuestionConfig(props.questionConfigs, questionIds.WeaknessFurtherDetails)
                                    ],
                                    readonly: props.readonly,
                                    questions: props.questions
                                })}
                            </>
                            }
                            {isOldVersion &&
                                <MultiCheckboxQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DominantHand)}
                                    mandatory
                                >
                                    <MultiCheckbox label="Right handed" value="Right handed" />
                                    <MultiCheckbox label="Left handed" value="Left handed" />
                                </MultiCheckboxQuestion>
                            }
                            {!isOldVersion &&
                                <MultiButtonQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DominantHand)}
                                    values={RIGHT_LEFT_HAND_OPTIONS}
                                />
                            }
                            {isOldVersion &&
                                <MultiCheckboxQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeadingHand)}
                                    mandatory
                                >
                                    <MultiCheckbox label="Right" value="Right" />
                                    <MultiCheckbox label="Left" value="Left" />
                                </MultiCheckboxQuestion>
                            }
                            {!isOldVersion &&
                                <MultiButtonQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeadingHand)}
                                    values={RIGHT_LEFT_OPTIONS}
                                />
                            }
                            <TextQuestion
                                readonly={props.readonly}
                                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.FirstStartUsingVibrating)}
                                mandatory={isOldVersion}
                            />
                            <TextQuestion
                                readonly={props.readonly}
                                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.WhereNoticeVibrationMost)}
                                mandatory={isOldVersion}
                            />
                            <TextQuestion
                                readonly={props.readonly}
                                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.WhenStopUsingVibratingTools)}
                                mandatory={isOldVersion}
                            />
                            <TextQuestion
                                readonly={props.readonly}
                                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.MainElementsJobInvolvingVibratingTools)}
                                mandatory={isOldVersion}
                            />
                            <OldDateQuestion
                                readonly={props.readonly}
                                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.WhenJoinCompany)}
                                mandatory={isOldVersion}
                            />
                            <TextQuestion
                                readonly={props.readonly}
                                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.MainJobsAndDepartments)}
                                mandatory={isOldVersion}
                            />
                            {isOldVersion &&
                                <TextQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PreviousJobsInvolvingVibrations)}
                                    mandatory
                                />
                            }
                            {!isOldVersion &&
                                <TextQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PreviousJobsInvolvingVibrationsUpdated)}
                                />
                            }
                            {YesNoWithFollowUpQuestion({
                                questionConfigs: [
                                    getQuestionConfig(props.questionConfigs, questionIds.ExposureToChemicalsAtWork),
                                    getQuestionConfig(props.questionConfigs, questionIds.ExposureToChemicalsAtWorkFurtherDetails)
                                ],
                                questions: props.questions,
                                readonly: props.readonly
                            })}
                            {YesNoWithFollowUpQuestion({
                                questionConfigs: [
                                    getQuestionConfig(props.questionConfigs, questionIds.HandArmVibrationHobbies),
                                    getQuestionConfig(props.questionConfigs, questionIds.HandArmVibrationHobbiesFurtherDetails)
                                ],
                                readonly: props.readonly,
                                questions: props.questions
                            })}
                            {YesNoWithFollowUpQuestion({
                                questionConfigs: [
                                    getQuestionConfig(props.questionConfigs, questionIds.DoYouSmoke),
                                    getQuestionConfig(props.questionConfigs, questionIds.HowManySmokePerDay)
                                ],
                                readonly: props.readonly,
                                questions: props.questions
                            })}
                            {YesNoWithFollowUpQuestion({
                                questionConfigs: [
                                    getQuestionConfig(props.questionConfigs, questionIds.ExSmoker),
                                    getQuestionConfig(props.questionConfigs, questionIds.ExSmokerWhenQuit)
                                ],
                                readonly: props.readonly,
                                questions: props.questions
                            })}
                            {YesNoWithFollowUpQuestion({
                                questionConfigs: [
                                    getQuestionConfig(props.questionConfigs, questionIds.DrinkAlcohol),
                                    getQuestionConfig(props.questionConfigs, questionIds.DrinkAlcoholNumberOfUnitsPerWeek)
                                ],
                                readonly: props.readonly,
                                questions: props.questions
                            })}
                            {YesNoWithFollowUpQuestion({
                                questionConfigs: [
                                    getQuestionConfig(props.questionConfigs, questionIds.CloseFamilyVibrationWhiteFinger),
                                    getQuestionConfig(props.questionConfigs, questionIds.CloseFamilyVibrationWhiteFingerWho)
                                ],
                                readonly: props.readonly,
                                questions: props.questions
                            })}
                            {isOldVersion &&
                                <>
                                    {YesNoWithFollowUpQuestion({
                                        questionConfigs: [
                                            getQuestionConfig(props.questionConfigs, questionIds.EverNeckArmHandInjuryOrOperation),
                                            getQuestionConfig(props.questionConfigs, questionIds.EverNeckArmHandInjuryOrOperationWhatWhen)
                                        ],
                                        readonly: props.readonly,
                                        questions: props.questions
                                    })}
                                    {getAnswerToQuestionWithId(questions, questionIds.EverNeckArmHandInjuryOrOperation) === MultiButtonQuestion.YesOption &&
                                        <>
                                            {YesNoWithFollowUpQuestion({
                                                questionConfigs: [
                                                    getQuestionConfig(props.questionConfigs, questionIds.EverNeckArmHandInjuryOrOperationAnyProblems),
                                                    getQuestionConfig(
                                                        props.questionConfigs,
                                                        questionIds.EverNeckArmHandInjuryOrOperationAnyProblemsFurtherDetails)
                                                ],
                                                readonly: props.readonly,
                                                questions: props.questions
                                            })}
                                        </>
                                    }
                                </>
                            }
                            {!isOldVersion &&
                                <>
                                    {YesNoWithFollowUpQuestion({
                                        questionConfigs: [
                                            getQuestionConfig(props.questionConfigs, questionIds.EverNeckArmHandInjuryOrOperationUpdated),
                                            getQuestionConfig(props.questionConfigs, questionIds.EverNeckArmHandInjuryOrOperationWhatWhen)
                                        ],
                                        readonly: props.readonly,
                                        questions: props.questions
                                    })}
                                    {getAnswerToQuestionWithId(questions, questionIds.EverNeckArmHandInjuryOrOperationUpdated)
                                        === MultiButtonQuestion.YesOption &&
                                        <>
                                            {YesNoWithFollowUpQuestion({
                                                questionConfigs: [
                                                    getQuestionConfig(props.questionConfigs, questionIds.EverNeckArmHandInjuryOrOperationAnyProblems),
                                                    getQuestionConfig(
                                                        props.questionConfigs,
                                                        questionIds.EverNeckArmHandInjuryOrOperationAnyProblemsFurtherDetails)
                                                ],
                                                readonly: props.readonly,
                                                questions: props.questions
                                            })}
                                        </>
                                    }
                                </>
                            }
                            <strong>Have you ever had any serious disease of:</strong>
                            {YesNoWithFollowUpQuestion({
                                questionConfigs: [
                                    getQuestionConfig(props.questionConfigs, questionIds.SeriousDiseaseJoints),
                                    getQuestionConfig(props.questionConfigs, questionIds.SeriousDiseaseJointsFurtherDetails)
                                ],
                                readonly: props.readonly,
                                questions: props.questions
                            })}
                            {YesNoWithFollowUpQuestion({
                                questionConfigs: [
                                    getQuestionConfig(props.questionConfigs, questionIds.SeriousDiseaseSkin),
                                    getQuestionConfig(props.questionConfigs, questionIds.SeriousDiseaseSkinFurtherDetails)
                                ],
                                readonly: props.readonly,
                                questions: props.questions
                            })}
                            {YesNoWithFollowUpQuestion({
                                questionConfigs: [
                                    getQuestionConfig(props.questionConfigs, questionIds.SeriousDiseaseNerves),
                                    getQuestionConfig(props.questionConfigs, questionIds.SeriousDiseaseNervesFurtherDetails)
                                ],
                                readonly: props.readonly,
                                questions: props.questions
                            })}
                            {YesNoWithFollowUpQuestion({
                                questionConfigs: [
                                    getQuestionConfig(props.questionConfigs, questionIds.SeriousDiseaseHeartBloodVessels),
                                    getQuestionConfig(props.questionConfigs, questionIds.SeriousDiseaseHeartBloodVesselsFurtherDetails)
                                ],
                                readonly: props.readonly,
                                questions: props.questions
                            })}
                            {YesNoWithFollowUpQuestion({
                                questionConfigs: [
                                    getQuestionConfig(props.questionConfigs, questionIds.SeriousDiseaseOther),
                                    getQuestionConfig(props.questionConfigs, questionIds.SeriousDiseaseOtherFurtherDetails)
                                ],
                                readonly: props.readonly,
                                questions: props.questions
                            })}
                            {YesNoWithFollowUpQuestion({
                                questionConfigs: [
                                    getQuestionConfig(props.questionConfigs, questionIds.AnyLongTermMedicationTreatmentForCondition),
                                    getQuestionConfig(props.questionConfigs, questionIds.AnyLongTermMedicationTreatmentForConditionFurtherDetails)
                                ],
                                readonly: props.readonly,
                                questions: props.questions
                            })}
                        </>
                    }
                </>}
        </OldProgressBarQuestionnaireSectionUnconnected>
    );
};

export const HealthSurveillanceConsultationHavsSection =
    connect(makeMapStateToQuestionnaireSectionProps(sections.Havs.id), mapDispatchToQuestionnaireSectionProps)(HealthSurveillanceConsultationHavsSectionUnconnected); // eslint-disable-line max-len
